import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { DeleteOutlined, EditOutlined, MoreVertOutlined, VisibilityOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import { PermissionActionType } from "@/constants/permissions"
import type { ProductTest } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { withClose } from "@/utils/withClose"

interface ProductTestRowActionsProps {
  row: CellContext<ProductTest, unknown>["row"]
  onEdit: (productTest: ProductTest) => void
  onDelete: (productTest: ProductTest) => void
  onViewHistory: (productTest: ProductTest) => void
}

const ProductTestRowActions: FC<ProductTestRowActionsProps> = ({ row, onEdit, onDelete, onViewHistory }) => {
  const { hasPermissions } = usePermissionManager()
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleDropdownOpen = () => setIsDropdownOpen(true)
  const handleDropdownClose = () => setIsDropdownOpen(false)

  const handleEdit = withClose(handleDropdownClose)(() => onEdit(row.original))
  const handleDelete = withClose(handleDropdownClose)(() => onDelete(row.original))
  const handleViewHistory = withClose(handleDropdownClose)(() => onViewHistory(row.original))

  const hasHistoryLoaded = Boolean(row.original.history && row.original.history.length > 0)

  return (
    <Box ref={anchorElRef}>
      <IconButton size='small' onClick={handleDropdownOpen} data-test='row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isDropdownOpen}
        onClose={handleDropdownClose}
        data-test='row-actions-menu'
      >
        {hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE]) && (
          <>
            <MenuItem data-test='update-test-data-menu-item' onClick={handleEdit}>
              <ListItemIcon>
                <EditOutlined />
              </ListItemIcon>
              <ListItemText primary='Update Test Data' />
            </MenuItem>
            <MenuItem data-test='remove-lot-menu-item' onClick={handleDelete}>
              <ListItemIcon>
                <DeleteOutlined />
              </ListItemIcon>
              <ListItemText primary='Remove Lot' />
            </MenuItem>
          </>
        )}
        <MenuItem data-test='view-lot-variations-menu-item' onClick={handleViewHistory}>
          <ListItemIcon>
            <VisibilityOutlined />
          </ListItemIcon>
          <ListItemText primary={hasHistoryLoaded ? "Hide Lot Variations" : "View Lot Variations"} />
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default ProductTestRowActions
